import {Breadcrumb, Button, Col, Input, Row, Space, Table, Tooltip} from 'antd';
import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';
import {ReactComponent as Back} from '../../Images/Back.svg';
import {fetchConversionRateList, resetConversionRateList} from '../../slices/conversion-rate';
import '../../styles/Main.scss';
import {
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {ReactComponent as FilterIcon} from '../../Images/FilterIcon.svg';
import {debounce} from 'lodash';
import * as XLSX from 'xlsx';
import {createActivityLog} from '../../slices/activity-log';
import ImportDataModal from '../../Common/ImportDataModal';
import logo from '../../Images/logo.svg';
import formatFile from "../../Images/sample-files/conversion-rate-format.xlsx";

function ViewRateList() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const {data = [], totalRecord} = useSelector(state => state.conversionRate);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [inputValue, setInputValue] = useState();
  const {user: currentUser} = useSelector(state => state.auth);
  const ipAddress = useSelector(state => state.ip.ipAddress);

  useEffect(() => {
    dispatch(
      fetchConversionRateList({
        page,
        pagesize,
        ...(search && {search}),
        ...(orderBy && {ordering: orderBy}),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);

  const refreshTable = () => {
      dispatch(resetConversionRateList());
      dispatch(
        fetchConversionRateList({
          page: 1,
          pagesize,
          ...(search && { search }),
          ...(orderBy && { ordering: orderBy }),
        })
      );
    };
  const calculateRowSpanAndSrNo = data => {
    const rowSpanMap = {};
    const srNoMap = {};
    let currentCustomer = null;
    let currentSpan = 1;
    let startIndex = 0;
    let srNoCounter = 1;

    data.forEach((item, index) => {
      const customerName = item.customer || '';

      if (currentCustomer === null) {
        currentCustomer = customerName;
        startIndex = index;
        srNoMap[index] = srNoCounter++;
      } else if (currentCustomer === customerName) {
        currentSpan++;
        rowSpanMap[index] = 0;
      } else {
        rowSpanMap[startIndex] = currentSpan;
        currentCustomer = customerName;
        currentSpan = 1;
        startIndex = index;
        srNoMap[index] = srNoCounter++;
      }
    });

    if (currentSpan > 0) {
      rowSpanMap[startIndex] = currentSpan;
    }

    return {rowSpanMap, srNoMap};
  };

  const activityLog = () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Conversion Rate',
        ip_address: ipAddress,
        description: 'Conversion rate Excel downloaded successfully!',
      }),
    );
  };

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1,
  }));

  const {rowSpanMap, srNoMap} = calculateRowSpanAndSrNo(dataSource);

  const debouncedAPICall = useMemo(
    () =>
      debounce(query => {
        if (query.length >= 3 || query.length === 0) {
          setSearch(query);
          localStorage.setItem('die_text_search_converstionRate', query);
          setPage(1);
        }
      }, 500),
    [],
  );

  const handleSearch = e => {
    const value = e.target.value;
    setInputValue(value);
    debouncedAPICall(value);
  };

  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy('');
    localStorage.removeItem('die_text_search_converstionRate');
    localStorage.removeItem('die_group_search_converstionRate');
    setPage(1);
  };

  const handleExcelDownload = () => {
    const headers = [
      'Sr. No.',
      'Customer Name',
      'Profile No',
      'Conversion',
      'Alloy',
      'Temper',
      'Remarks',
    ];

    const dataRows = [];
    let srNoCounter = 1;
    let currentCustomer = null;

    dataSource.forEach((item, index) => {
      const customerName = item.customer || '';
      const isSameCustomer = customerName === currentCustomer;

      dataRows.push([
        isSameCustomer ? '' : srNoCounter++,
        isSameCustomer ? '' : customerName,
        item.die || 'N/A',
        item.conversion || '',
        item.alloy || 'N/A',
        item.temper || 'N/A',
        item.remarks || '',
      ]);

      if (!isSameCustomer) {
        currentCustomer = customerName;
      }
    });

    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const merges = [];
    let custStartRow = 1;

    for (let i = 1; i < dataRows.length; i++) {
      const currCustomer = dataRows[i][1];
      const prevCustomer = dataRows[i - 1][1];

      if (currCustomer !== '' && currCustomer !== prevCustomer) {
        if (i - custStartRow > 0) {
          merges.push({
            s: {r: custStartRow, c: 0},
            e: {r: i, c: 0},
          });
          merges.push({
            s: {r: custStartRow, c: 1},
            e: {r: i, c: 1},
          });
        }
        custStartRow = i + 1;
      }
    }
    if (dataRows.length - custStartRow > 0) {
      merges.push({
        s: {r: custStartRow, c: 0},
        e: {r: dataRows.length, c: 0},
      });
      merges.push({
        s: {r: custStartRow, c: 1},
        e: {r: dataRows.length, c: 1},
      });
    }

    ws['!merges'] = merges;

    ws['!cols'] = [
      {wch: 8},
      {wch: 25},
      {wch: 15},
      {wch: 12},
      {wch: 12},
      {wch: 12},
      {wch: 30},
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Conversion Rate');

    XLSX.writeFile(
      wb,
      `Conversion_Rate_${new Date().toISOString().split('T')[0]}.xlsx`,
    );
  };

  const generatePrintRows = () => {
    const rows = [];
    let currentCustomer = null;
    let rowSpan = 0;
    let srNoCounter = 1;
    let spanStartIndex = 0;

    const spanData = [];
    dataSource.forEach((item, index) => {
      const customer = item.customer || '';
      if (customer === currentCustomer) {
        rowSpan++;
      } else {
        if (rowSpan > 0) {
          spanData.push({index: spanStartIndex, span: rowSpan});
        }
        currentCustomer = customer;
        rowSpan = 1;
        spanStartIndex = index;
      }
    });
    if (rowSpan > 0) {
      spanData.push({index: spanStartIndex, span: rowSpan});
    }

    dataSource.forEach((item, index) => {
      const customer = item.customer || '';
      const isSpanStart = spanData.some(s => s.index === index);
      const spanObj = spanData.find(s => s.index === index);

      // Determine how to render die_diagram
      let diagramCell = 'N/A';
      if (item.die_diagram) {
        const extension = item.die_diagram.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png'].includes(extension)) {
          diagramCell = `<img src="${item.die_diagram}" style="width:80px;height:auto;" />`;
        } else {
          diagramCell = '';
        }
      }

      rows.push(`<tr>
            ${isSpanStart ? `<td rowspan="${spanObj.span}">${srNoCounter++}</td>` : ''}
            ${isSpanStart ? `<td rowspan="${spanObj.span}">${customer}</td>` : ''}
            <td>${item.die || 'N/A'}</td>
            <td>${diagramCell}</td>
            <td>${item.conversion || ''}</td>
            <td>${item.alloy || 'N/A'}</td>
            <td>${item.temper || 'N/A'}</td>
            <td>${item.remarks || ''}</td>
        </tr>`);
    });

    return rows.join('\n');
  };

  const handlePrint = () => {
    const rows = generatePrintRows();
    const currentDateTime = new Date().toLocaleString();
    const printWindow = window.open('', '', 'width=1200,height=900');

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Conversion Rate</title>
        <style>
          body { 
              font-family: Arial, sans-serif;
              font-size: 20px;
              line-height: 1.2;
              color: #000;
              margin: 10px;
          }
          table { 
              width: 100%; 
              border-collapse: collapse; 
          }
          table, th, td {
              border: 1px solid #333;
          }
          th, td { 
             padding: 4px;
             text-align: center;
             font-size: 18px;
          }
          th { 
              background: #12406a; 
              color: #fff; 
          }
          tr:nth-child(even) { 
              background: #f9f9f9; 
          }
      
          tr, td {
          page-break-inside: avoid;
         }
          @page {
            size: A4 landscape;
            margin: 20mm;
          }

          /* Logo - top-right (first page only) */
  
          .logo {
          position: absolute; /* or just remove position */

            top: 10px;
            right: 20px;
          }
          .logo img {
            height: 50px;
          }
          @media print {
            body {
              counter-reset: page;
            }
            /* hide logo after first page */
            .logo {
              display: none;
            }
             .logo:first-of-type {
              display: block;
              }
            .footer {
              position: fixed;
              bottom: 10px;
              left: 20px;
              right: 20px;
              font-size: 14px;
              display: flex;
              justify-content: space-between;
            }
            .pagenum:after {
              counter-increment: page;
              content: "Page " counter(page);
            }
          }
        </style>
      </head>
      <body>
        <!-- Logo (only on first page) -->
        <div class="logo">
          <img src="${logo}" alt="Logo" />
        </div>

        <h2 style="text-align: center;">Conversion Rate List</h2>
        <table>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Customer</th>
              <th>Profile No</th>
              <th>Profile Image</th>
              <th>Conversion</th>
              <th>Alloy</th>
              <th>Temper</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>

        <!-- Footer -->
        <div class="footer">
          <span>${currentDateTime}</span>
          <span class="pagenum"></span>
        </div>
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      sorter: true,
      render: (text, record, index) => ({
        children: srNoMap[index] ? <span>{srNoMap[index]}</span> : '',
        props: {rowSpan: rowSpanMap[index] || 0},
      }),
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer, record, index) => ({
        children: customer ? <span>{customer}</span> : '',
        props: {rowSpan: rowSpanMap[index] || 0},
      }),
    },
    {
      title: 'Profile No',
      dataIndex: 'die',
      key: 'die',
      render: die => <span>{die || 'N/A'}</span>,
    },
    {
      title: 'Profile Image',
      dataIndex: 'die_diagram',
      key: 'die_diagram',
      render: (text, record) => (
        <img
          src={record.die_diagram}
          alt="Die Diagram"
          style={{width: '100px', height: 'auto'}}
        />
      ),
    },
    {
      title: 'Conversion',
      dataIndex: 'conversion',
      key: 'conversion',
      sorter: true,
    },
    {
      title: 'Alloy',
      dataIndex: 'alloy',
      key: 'alloy',
      sorter: true,
      render: alloy => <span>{alloy || 'N/A'}</span>,
    },
    {
      title: 'Temper',
      dataIndex: 'temper',
      key: 'temper',
      sorter: true,
      render: temper => <span>{temper || 'N/A'}</span>,
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      sorter: true,
    },
  ];

  const handlePageChange = (newPage, newPageSize) => {
    setPage(newPage);
    setPageSize(newPageSize);
    setOrderBy('');
  };

  return (
    <>
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2 style={{margin: 0}}>View Rate List</h2>
            <div className="bredcrumbwrp">
              <Link onClick={() => navigate(-1)} className="back">
                <Back /> BACK
              </Link>

              <Breadcrumb
                items={[
                  {title: <Link to="/dashboard">Home</Link>},
                  {
                    title: (
                      <Link to="/conversion-rate-list">
                        Conversation Rate List
                      </Link>
                    ),
                  },
                  {title: 'View Rate List'},
                ]}
              />
            </div>
          </Col>

          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={search}
                style={{width: 200}}
                onChange={handleSearch}
              />
              <Tooltip title="Reset Filters">
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>

              {currentUser.permissions.permissions.die.includes(
                'download_conversion_rate_excel_copy',
              ) && (
                <Tooltip placement="top" title="Export Excel">
                  <Button
                    type="primary"
                    className="iconlink"
                    onClick={() => {
                      handleExcelDownload();
                      activityLog();
                    }}
                  >
                    <FileExcelOutlined />
                  </Button>
                </Tooltip>
              )}
              {currentUser.permissions.permissions.die.includes(
                'download_conversion_rate_pdf_copy',
              ) && (
                <Tooltip placement="top" title="Print">
                  <Button
                    type="primary"
                    className="iconlink"
                    onClick={() => handlePrint()}
                  >
                    <FilePdfOutlined />
                  </Button>
                </Tooltip>
              )}
                {/* <Button type="primary" icon={<UploadOutlined  />} onClick={() => setOpenModal(true)}>
                Import Data
              </Button> */}

               <ImportDataModal
                      open={openModal}
                      onClose={() => setOpenModal(false)}
                      refreshTable={refreshTable}
                      formatFilePath={formatFile}
                      model={"conversionrate"}
                    />
              
            </Space>
          </Col>
        </Row>
      </div>
      <div className="formwrapper printable-area">
        <Table
          className="px-0"
          dataSource={dataSource}
          columns={columns}
          pagination={{
            current: page,
            pageSize: pagesize,
            total: totalRecord,
            showTotal: total => `Total ${total} items`,
            showSizeChanger: true,
            onChange: handlePageChange,
            locale: {
              items_per_page: 'Items / Page',
            },
          }}
          scroll={{x: 880}}
        />
      </div>
    </>
  );
}

export default ViewRateList;
